import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, Stack } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import useFinanceDataStore from "@/store/financeData";
import {
  parseDisplayToCents,
  handleCurrencyInputChange,
} from "@/utils/myMoney";
import { showError } from "@/utils/toast";

const SetMonthlyBudgetScreen = () => {
  const { updateBudget } = useFinanceDataStore();
  const [budgetValue, setBudgetValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleBack = () => {
    router.back();
  };

  const handleBudgetChange = (text: string) => {
    setBudgetValue(handleCurrencyInputChange(text));
  };

  const handleSave = async () => {
    const budgetCents = parseDisplayToCents(budgetValue);
    console.log("[SetMonthlyBudget] Saving budget:", budgetValue, "cents:", budgetCents);

    if (budgetCents <= 0) {
      showError("Please enter a valid budget amount");
      return;
    }

    setIsSaving(true);
    try {
      console.log("[SetMonthlyBudget] Calling updateBudget...");
      const success = await updateBudget(budgetCents);
      console.log("[SetMonthlyBudget] updateBudget returned:", success);
      if (success) {
        router.push("/signup/set-category-budget");
      } else {
        showError("Failed to save budget");
      }
    } catch (error) {
      console.error("[SetMonthlyBudget] Error:", error);
      showError("Failed to save budget");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
    >
      <Stack.Screen options={{ headerShown: false, gestureEnabled: true }} />
      <StatusBar barStyle="dark-content" />
        <SafeAreaView style={styles.safeArea}>
          {/* Back Button */}
          <View style={styles.headerContainer}>
            <TouchableOpacity style={styles.backButton} onPress={handleBack}>
              <Ionicons name="chevron-back" size={28} color="#161D14" />
            </TouchableOpacity>
          </View>

          {/* Content */}
          <View style={styles.content}>
            {/* Header */}
            <View style={styles.headerSection}>
              <Text style={styles.title}>Set your total monthly budget</Text>
              <Text style={styles.subtitle}>
                Start by setting your total monthly budget. Sparrow will help you
                track spending and keep you within the plan.
              </Text>
            </View>

            {/* Budget Input */}
            <View style={styles.inputContainer}>
              <TextInput
                style={styles.budgetInput}
                value={budgetValue}
                onChangeText={handleBudgetChange}
                keyboardType="decimal-pad"
                returnKeyType="done"
                onSubmitEditing={Keyboard.dismiss}
                autoFocus
                placeholder="$0.00"
                placeholderTextColor="rgba(22, 29, 20, 0.4)"
                selection={{ start: budgetValue.length, end: budgetValue.length }}
              />
              <View style={styles.inputUnderline} />
            </View>
          </View>

          {/* Spacer */}
          <View style={styles.spacer} />

          {/* Bottom Section */}
          <View style={styles.bottomSection}>
            <ActionButton
              title={isSaving ? "" : "Save"}
              onPress={handleSave}
              disabled={isSaving}
            >
              {isSaving && <ActivityIndicator color="#FFFFFF" />}
            </ActionButton>
          </View>
        </SafeAreaView>
    </KeyboardAvoidingView>
  );
};

export default SetMonthlyBudgetScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  safeArea: {
    flex: 1,
  },
  headerContainer: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  backButton: {
    width: 28,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  headerSection: {
    gap: 12,
    marginBottom: 32,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 28,
    lineHeight: 34,
    color: "#161D14",
    letterSpacing: -0.56,
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    lineHeight: 22,
    color: "rgba(22, 29, 20, 0.7)",
    letterSpacing: 0.15,
  },
  inputContainer: {
    marginTop: 8,
  },
  budgetInput: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 24,
    lineHeight: 31,
    color: "#161D14",
    padding: 0,
    paddingBottom: 12,
  },
  inputUnderline: {
    height: 1,
    backgroundColor: "#0F9129",
  },
  spacer: {
    flex: 1,
  },
  bottomSection: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
});
